import React from 'react';
import { motion } from 'framer-motion';
import { Bot, Instagram, Smartphone, Award, DollarSign, Zap, Database, Receipt, Users } from 'lucide-react';
import { PhotoCard } from '../UI/PhotoCard';
import { useTranslation } from 'react-i18next';
import restoCardPhotos from '../../config/restoCardPhotos.json';

export const RestoBenefits: React.FC = () => {
  const { t } = useTranslation(['resto', 'common']);
  const isBlack = document.documentElement.classList.contains('theme-black');
  const { opacity, opacityHover, benefits: photos } = restoCardPhotos;

  const benefits = [
    {
      key: 'aiAutomation' as const,
      icon: Bot,
      title: t('resto:benefits.aiAutomation.title'),
      description: t('resto:benefits.aiAutomation.description'),
    },
    {
      key: 'growthHacking' as const,
      icon: Instagram,
      title: t('resto:benefits.growthHacking.title'),
      description: t('resto:benefits.growthHacking.description'),
    },
    {
      key: 'mobileApp' as const,
      icon: Smartphone,
      title: t('resto:benefits.mobileApp.title'),
      description: t('resto:benefits.mobileApp.description'),
    },
    {
      key: 'patent' as const,
      icon: Award,
      title: t('resto:benefits.patent.title'),
      description: t('resto:benefits.patent.description'),
    },
    {
      key: 'massiveSavings' as const,
      icon: DollarSign,
      title: t('resto:benefits.massiveSavings.title'),
      description: t('resto:benefits.massiveSavings.description'),
    },
    {
      key: 'ultraFastDeployment' as const,
      icon: Zap,
      title: t('resto:benefits.ultraFastDeployment.title'),
      description: t('resto:benefits.ultraFastDeployment.description'),
    },
    {
      key: 'sharedDatabase' as const,
      icon: Database,
      title: t('resto:benefits.sharedDatabase.title'),
      description: t('resto:benefits.sharedDatabase.description'),
    },
    {
      key: 'billing' as const,
      icon: Receipt,
      title: t('resto:benefits.billing.title'),
      description: t('resto:benefits.billing.description'),
    },
    {
      key: 'multiAccount' as const,
      icon: Users,
      title: t('resto:benefits.multiAccount.title'),
      description: t('resto:benefits.multiAccount.description'),
    },
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      {!isBlack && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10 dark:hidden"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/260922/pexels-photo-260922.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
          }}
        />
      )}
      <div className={`absolute inset-0 ${isBlack ? 'bg-transparent' : 'bg-white/90 dark:bg-komby-yellow'}`} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
          >
            {t('resto:benefits.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-xl text-gray-800 max-w-3xl mx-auto"
          >
            {t('resto:benefits.subtitle')}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <PhotoCard
                imageSrc={photos[benefit.key]}
                opacity={opacity}
                opacityHover={opacityHover}
                className="h-full min-h-[16rem]"
              >
                <div className="mb-4 flex justify-center">
                  <div className="rounded-xl border border-accent1/40 bg-black/35 p-3 backdrop-blur-sm">
                    <benefit.icon className="h-8 w-8 text-accent1" />
                  </div>
                </div>
                <h3 className="mb-3 text-center text-xl font-semibold text-white">
                  {benefit.title}
                </h3>
                <p className="text-justify leading-relaxed text-gray-200">
                  {benefit.description}
                </p>
              </PhotoCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
